import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, combineLatest, distinctUntilChanged, takeUntil } from 'rxjs';
import { Router } from '@angular/router';

import {
  selectCalLotsSlice,
  selectCalculationAdjustedResult,
  selectIsCalAdjustFinal,
  selectCalculationResult,
  selectPanelName,
  selectPanelSpecsSlice,
  selectPatientFileMetadata,
  selectRefKitLot,
  selectReportContentItemsSlice,
  selectSampleDataLoading,
  selectAssayDetailsColumnSelection,
  selectSampleDataAdjusted,
  selectSampleDataFailuresAdjusted,
  selectSampleDataFailuresOriginal,
  selectSampleDataOriginal,
  selectSampleDataStatus,
  selectRunType,
  selectTestSpec,
  selectQcLotsSlice,
  selectAppState,
  selectIsStudySaved
} from '../../state/selectors';
import { ensureSampleData } from '../../state/actions';
import { FileMetaData, ReportContentItem } from '../../../shared/models/run.model';
import { AssayDetails, CalculationResult, RegressionStats, ReportItems, RunStatus, SampleSummaryResult } from '../../../shared/models/result.model';
import { ColumnSelection, CommonDialogData } from '../../../shared/models/dialog.model';
import {
  applyPrintColumnSelection,
  chunkColumnsForPrint,
  describePrintPage,
  PrintPageFit
} from '../../../shared/utils/print-column.util';
import { AppUtilityService } from '../../../shared/services/app-utility.service';
import { trackByFn, getStatusClassFromResult, runValidityPassFailAlertCount, resultPassFailAlert, normalizeStatusValue } from '../../../shared/utils/assay-helper ';
import { Panel } from '../../../shared/models/assays.model';
import { CalLot } from '../../../shared/models/cal-lot.model';
import { QcLot } from '../../../shared/models/qc-lot.model';
import { formatDiscardReason } from '../../../shared/utils/discard-reason.util';
import {
  FailureSection,
  FailureSectionRow,
  breachedRange,
  concentrationLevel,
  describeSpecRanges,
  hasSdCvFailure,
  isFalseScore,
  isReplicateCountShort,
  keptReps,
  repConcentration,
  repNumber,
  resultLevel,
  sdCvLevels
} from './assay-failures.util';
import { adjustedColumnLabel } from '../../../shared/utils/helper';
import { isStabilityRunType } from '../../../shared/utils/run-type.util';

/** Text colour for a sample listed because of an auto-discard rather than a spec failure. */
/** A replicate the app discarded automatically - not a spec failure, so not the fail colour. */
const DISCARD_TEXT_CLASS = 'assay-details__discard-text';
/**
 * Component for displaying Assay Failures.
 * It loads relevant calculation results, report content, and metadata
 * related to the selected study.
 * 
 * Each section lists only the samples that fail that section's own check, so the sections are
 * self-contained rather than sharing one list of accessions.
 *
 * Supports print-friendly view modes.
 */
@Component({
  selector: 'app-assay-failures',
  templateUrl: './assay-failures.component.html',
  styleUrl: './assay-failures.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssayFailuresComponent implements OnInit, OnDestroy, AfterViewInit {
  isStudySaved$ = this.store.select(selectIsStudySaved);
  @Input() tableId = 'assay-table';
  @Input() exportMode = false;
  @ViewChild('assayDetailsScroll', { static: false }) assayDetailsScrollRef?: ElementRef<HTMLDivElement>;
  @ViewChild('assayDetailsTable', { static: false }) assayDetailsTableRef?: ElementRef<HTMLTableElement>;
  // In print mode the table is rendered once per chunk page, and every instance needs pruning.
  @ViewChildren('assayDetailsTable') assayDetailsTableRefs?: QueryList<ElementRef<HTMLTableElement>>;

  panelName$ = this.store.select(selectPanelName);
  /** Sections the operator has collapsed; keyed by id because sections come and go with the data. */
  collapsedSections = new Set<string>();
  patientFileMetadata: FileMetaData[] = [];
  reportContent: ReportContentItem[] = [];
  filteredReportContent: ReportContentItem[] = [];
  calculationResponse: CalculationResult | null = null;
  calculationAdjustedResponse: CalculationResult | null = null;
  tableColumns: ColumnSelection[] = [];
  /** Null until the operator narrows the print selection; see `applyPrintColumnSelection`. */
  private printColumnSelection: ColumnSelection[] | null = null;
  selectedColumns!: ColumnSelection[];
  isPrintView = false;
  isPrintMode = false;
  printTruncatedReport = true; /** Print Truncated Report checkbox state */

  /** Print View, Print Truncated Report and Export As Workbook are offered for Stability runs only. */
  get showStabilityOutputs(): boolean {
    return isStabilityRunType(this.runType);
  }
  isMenuOpen = false;
  operatorName = '';
  studyName = '';
  specVersion = '';
  appVersion = '';
  protected destroy$ = new Subject<void>();
  /** Whether the adjusted columns hold a Calibrator Adjustment's Final run. */
  isFinal = false;

  get adjustedLabel(): string {
    return adjustedColumnLabel(this.isFinal);
  }
  private lastSampleDataFresh: boolean | null = null;
  calResponseFilteredData: AssayDetails[] = [];
  calResponseCombinedData: AssayDetails[] = [];
  calResponseCombinedBackupData: AssayDetails[] = [];
  calResponseFilteredAdjustedData: AssayDetails[] = [];
  calResponseFailures: AssayDetails[] = [];
  calResponseFailuresBaseline: AssayDetails[] = [];
  private sectionsSource: AssayDetails[] | null = null;
  private sectionsValue: FailureSection[] = [];
  private readonly sampleLookupCache = new WeakMap<SampleSummaryResult[], Map<string, SampleSummaryResult>>();
  private visibleSectionCount = 0;
  private deferredRenderHandle: number | null = null;
  private deferredRenderToken = 0;
  private readonly sampleFieldAliases: Record<string, string> = {};
  getStatusClassFromResult = getStatusClassFromResult;

  /**
   * The row labels here are sample IDs and statistic names; the data columns hold one value each.
   * Chunking is derived from these, not from a column count.
   */
  readonly printPageFit: PrintPageFit = { labelWidthMm: 58, columnWidthMm: 22 };

  trackByPage = (index: number) => index;

  get printPages(): AssayDetails[][] {
    return chunkColumnsForPrint(this.calResponseCombinedData, this.printPageFit);
  }

  /** "Columns 7-12 of 22 · page 2 of 4" over a chunk page; empty when the report was not split. */
  printPageCaption(pageIndex: number): string {
    return describePrintPage(pageIndex, this.printPages);
  }
  runValidityPassFailAlertCount = runValidityPassFailAlertCount;
  trackByFn = trackByFn;
  trackBySample = (_index: number, sample: string) => sample ?? _index;
  resultPassFailAlert = resultPassFailAlert;
  panelSpecs: Panel | null = null;
  qcLots: QcLot[] = [];
  calLots: CalLot[] = [];
  runType = '';
  testSpec = '';
  refKitLot = '';
  private readonly assayDetailsColumnSelection$ = this.store.select(selectAssayDetailsColumnSelection);
  sampleDataLoading = false;
  private readonly internalQcBeadRegions = new Set<number>([34, 98]);
  private headerResizeObserver?: ResizeObserver;
  sampleDataOriginal: SampleSummaryResult[] = [];
  sampleDataAdjusted: SampleSummaryResult[] = [];
  sampleDataFailuresOriginal: SampleSummaryResult[] = [];
  sampleDataFailuresAdjusted: SampleSummaryResult[] = [];
  private readonly runStatusMapCache = new WeakMap<RunStatus[], Map<string, RunStatus>>();
  private readonly regressionStatsMapCache = new WeakMap<RegressionStats[], Map<string, RegressionStats>>();
  private readonly reportItemsMapCache = new WeakMap<ReportItems[], Map<string, ReportItems>>();
  private readonly samplesByReportIdCache = new WeakMap<SampleSummaryResult[], Map<string, SampleSummaryResult[]>>();
  private lastRebuildInputs?: {
    reportContent: ReportContentItem[];
    sampleDataOriginal: SampleSummaryResult[];
    sampleDataAdjusted: SampleSummaryResult[];
    sampleDataFailuresOriginal: SampleSummaryResult[];
    sampleDataFailuresAdjusted: SampleSummaryResult[];
    calculationResponse: CalculationResult | null;
    calculationAdjustedResponse: CalculationResult | null;
    panelSpecs: Panel | null;
    calLots: CalLot[];
    qcLots: QcLot[];
    runType: string;
    testSpec: string;
    refKitLot: string;
    columnSelectionKey: string;
    isPrintView: boolean;
  };
  /**
   * 
   * @param router Angular router
   * @param store NGRX store
   * @param appUtilityService Utility service 
   * @param cdRef Change detector reference
   */
  constructor(
    private router: Router,
    private store: Store,
    private appUtilityService: AppUtilityService,
    public cdRef: ChangeDetectorRef
  ) { }

  /** Lifecycle hook: Component initialization. */
  ngOnInit() {
    this.store.select(selectIsCalAdjustFinal).pipe(takeUntil(this.destroy$)).subscribe(isFinal => {
      this.isFinal = isFinal;
      this.cdRef.markForCheck();
    });
    this.isPrintView = this.exportMode ? false : this.router?.url?.includes('print');
    // The print route is print mode; there is no other reason to be on it. This used to be
    // switched on by the print menu opening over the page, which left the layout in screen mode
    // if the operator dismissed the menu.
    this.isPrintMode = this.isPrintView;
    this.monitorSampleDataStatus();

    /* eslint-disable @ngrx/avoid-combining-selectors -- Component consumes individual slices to keep OnPush checks cheap. */
    combineLatest([
      this.store.select(selectPatientFileMetadata),
      this.store.select(selectReportContentItemsSlice),
      this.store.select(selectSampleDataOriginal),
      this.store.select(selectSampleDataAdjusted),
      this.store.select(selectSampleDataFailuresOriginal),
      this.store.select(selectSampleDataFailuresAdjusted),
      this.store.select(selectCalculationResult),
      this.store.select(selectCalculationAdjustedResult),
      this.store.select(selectPanelSpecsSlice),
      this.store.select(selectCalLotsSlice),
      this.store.select(selectQcLotsSlice),
      this.store.select(selectRunType),
      this.store.select(selectTestSpec),
      this.store.select(selectRefKitLot),
      this.assayDetailsColumnSelection$,
      this.store.select(selectAppState)
    ])
      .pipe(
        distinctUntilChanged((prev, curr) => prev.length === curr.length && prev.every((value, index) => value === curr[index])),
        takeUntil(this.destroy$)
      )
      .subscribe(([
      patientFileMetadata,
      reportContentResponse,
      sampleDataOriginal,
      sampleDataAdjusted,
      sampleFailuresOriginal,
      sampleFailuresAdjusted,
      calculationResult,
      calculationAdjustedResult,
      panelSpecs,
      calLots,
      qcLots,
      runType,
      testSpec,
      refKitLot,
      assayDetailsColumnSelection,
      appState
      ]) => {
        // Cache each slice locally so the template and helper methods can stay synchronous while still
        // taking advantage of the narrow store selectors.
      this.patientFileMetadata = patientFileMetadata ?? [];
      // Set before the sort below: the comparator reads refKitLot to pin the reference lot first.
      this.refKitLot = refKitLot ?? '';
      const reportContentItems = reportContentResponse?.reportContentItems ?? [];
      const sortedReportContent = this.sortReportContent(reportContentItems);
      this.reportContent = sortedReportContent;
      this.filteredReportContent = sortedReportContent;
      this.sampleDataOriginal = sampleDataOriginal ?? [];
      this.sampleDataAdjusted = sampleDataAdjusted ?? [];
      this.sampleDataFailuresOriginal = sampleFailuresOriginal ?? [];
      this.sampleDataFailuresAdjusted = sampleFailuresAdjusted ?? [];
      this.calculationResponse = calculationResult ?? null;
      this.calculationAdjustedResponse = calculationAdjustedResult ?? null;
      this.panelSpecs = panelSpecs;
      this.operatorName = appState?.loggedInUser ?? '';
      this.studyName = appState?.selectedStudy?.studyName ?? '';
      this.specVersion = appState?.selectedStudy?.panelSpecs?.revisionNumber ?? '';
      this.appVersion = appState?.appVersion ?? '';
      this.calLots = calLots ?? [];
      this.qcLots = qcLots ?? [];
      this.runType = runType ?? '';
      this.testSpec = testSpec ?? '';

      const nextColumnSelectionKey = this.buildColumnSelectionKey(assayDetailsColumnSelection);
      if (this.shouldRebuildAssayTable(nextColumnSelectionKey)) {
        this.rebuildAssayTable();
        this.applyStoredColumnSelection(assayDetailsColumnSelection);

        if (this.isPrintView) {
          this.toggleAllSections(true);
          // Carries the operator's choice across the rebuild. `applyStoredColumnSelection` above
          // works from the screen's stored selection, which is empty on this route, so it has just
          // re-selected every column. `tableColumns` is corrected too, not only the data derived
          // from it: it is what the preview bar binds its checkboxes to.
          this.tableColumns = applyPrintColumnSelection(this.tableColumns, this.printColumnSelection);
          this.handleSelectColumnsFailures(this.tableColumns);
        }
      }
      this.cdRef.markForCheck();
      this.scheduleEmptyRowPrune();
    });

    this.store.select(selectSampleDataLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe(sampleDataLoading => {
        this.sampleDataLoading = sampleDataLoading;
        this.cdRef.markForCheck();
      });
    /* eslint-enable @ngrx/avoid-combining-selectors */
  }

  /**
   * Lazily triggers pull_sample_data whenever a calculation makes cached rows stale.
   * Also shows a blocking spinner dialog whenever the backend call is running so the UI
   * never flashes empty tables while the data refreshes.
   */
  private monitorSampleDataStatus(): void {
    this.store.select(selectSampleDataStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        const becameStale = !status.sampleDataFresh && (this.lastSampleDataFresh === null || this.lastSampleDataFresh);
        if (becameStale && !status.sampleDataLoading) {
          this.store.dispatch(ensureSampleData());
        }
        this.lastSampleDataFresh = status.sampleDataFresh;
      });
  }

  ngAfterViewInit(): void {
    this.setupHeaderHeightObserver();
    this.scheduleEmptyRowPrune();
  }

  /**
   * Rebuilds the heavy table data whenever any of the store slices we care about changes.
   * Resets the cached arrays, regenerates the visible columns, and replays initialSetData so
   * we only do the expensive work when either the calculation payload or the selected tab changes.
   */
  private rebuildAssayTable(): void {
    this.collapsedSections = new Set<string>();
    this.resetDeferredSections();
    this.calResponseFilteredData = [];
    this.calResponseFilteredAdjustedData = [];
    this.calResponseCombinedData = [];
    this.calResponseCombinedBackupData = [];
    this.calResponseFailures = [];
    this.calResponseFailuresBaseline = [];

    if (!this.filteredReportContent.length) {
      this.tableColumns = [];
      return;
    }

    const selections = new Map<string, ColumnSelection>();
    const reportContent = this.filteredReportContent;
    reportContent.forEach(item => {
      const kitLot = item.lot;
      const instrumentId = item.runId;
      const beadRegion = item.region;
      const columnKey = this.buildColumnKey(kitLot, instrumentId, beadRegion);
      if (!selections.has(columnKey)) {
        selections.set(columnKey, {
          columnName: kitLot,
          columnKey,
          kitLot,
          instrumentId,
          beadRegion,
          isSelected: true
        });
      }
    });

    const orderedReportContent = [...reportContent].sort((a, b) => this.compareReportContentItems(a, b));
    const orderByKey = new Map<string, number>();
    orderedReportContent.forEach((item, index) => {
      const key = this.buildColumnKey(item.lot, item.runId, item.region);
      if (!orderByKey.has(key)) {
        orderByKey.set(key, index);
      }
    });

    this.tableColumns = Array.from(selections.values()).sort((a, b) => {
      const aOrder = orderByKey.get(a.columnKey ?? '') ?? Number.MAX_SAFE_INTEGER;
      const bOrder = orderByKey.get(b.columnKey ?? '') ?? Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });

    const originalSamplePool = this.sampleDataOriginal;
    this.initialSetData(this.calculationResponse, originalSamplePool, 'OriginalCalculate');

    if (this.calculationAdjustedResponse) {
      const adjustedSamplePool = this.sampleDataAdjusted;
      this.initialSetData(this.calculationAdjustedResponse, adjustedSamplePool, 'AdjustedCalculate');
    } else {
      this.calResponseFilteredAdjustedData = [];
      this.calResponseCombinedData = this.calResponseFilteredData;
      this.calResponseCombinedBackupData = this.calResponseFilteredData;
      this.calResponseFailures = this.calResponseCombinedData;
    }

    this.calResponseFailuresBaseline = [...this.calResponseCombinedData];
    this.calResponseFailures = this.calResponseCombinedData;

    if (this.isPrintView) {
      this.handleSelectColumnsFailures(this.tableColumns);
    }

    this.scheduleDeferredSections();
  }

  private resetDeferredSections(): void {
    this.cancelDeferredSections();
    this.visibleSectionCount = this.isPrintView || this.exportMode ? Number.MAX_SAFE_INTEGER : 3;
  }

  private scheduleDeferredSections(): void {
    if (this.isPrintView || this.exportMode) {
      return;
    }
    if (this.visibleSectionCount >= this.sections.length) {
      return;
    }
    const token = ++this.deferredRenderToken;
    const runBatch = () => {
      if (token !== this.deferredRenderToken) {
        return;
      }
      this.visibleSectionCount += 4;
      this.cdRef.markForCheck();
      this.scheduleEmptyRowPrune();
      if (this.visibleSectionCount < this.sections.length) {
        this.queueDeferredRender(runBatch);
      }
    };
    this.queueDeferredRender(runBatch);
  }

  private queueDeferredRender(callback: () => void): void {
    this.deferredRenderHandle = window.setTimeout(callback, 16);
  }

  private cancelDeferredSections(): void {
    this.deferredRenderToken += 1;
    if (this.deferredRenderHandle !== null) {
      window.clearTimeout(this.deferredRenderHandle);
    }
    this.deferredRenderHandle = null;
  }

  private shouldRebuildAssayTable(nextColumnSelectionKey: string): boolean {
    if (!this.lastRebuildInputs) {
      this.lastRebuildInputs = {
        reportContent: this.reportContent,
        sampleDataOriginal: this.sampleDataOriginal,
        sampleDataAdjusted: this.sampleDataAdjusted,
        sampleDataFailuresOriginal: this.sampleDataFailuresOriginal,
        sampleDataFailuresAdjusted: this.sampleDataFailuresAdjusted,
        calculationResponse: this.calculationResponse,
        calculationAdjustedResponse: this.calculationAdjustedResponse,
        panelSpecs: this.panelSpecs,
        calLots: this.calLots,
        qcLots: this.qcLots,
        runType: this.runType,
        testSpec: this.testSpec,
        refKitLot: this.refKitLot,
        columnSelectionKey: nextColumnSelectionKey,
        isPrintView: this.isPrintView
      };
      return true;
    }

    const last = this.lastRebuildInputs;
    const shouldRebuild = (
      last.reportContent !== this.reportContent ||
      last.sampleDataOriginal !== this.sampleDataOriginal ||
      last.sampleDataAdjusted !== this.sampleDataAdjusted ||
      last.sampleDataFailuresOriginal !== this.sampleDataFailuresOriginal ||
      last.sampleDataFailuresAdjusted !== this.sampleDataFailuresAdjusted ||
      last.calculationResponse !== this.calculationResponse ||
      last.calculationAdjustedResponse !== this.calculationAdjustedResponse ||
      last.panelSpecs !== this.panelSpecs ||
      last.calLots !== this.calLots ||
      last.qcLots !== this.qcLots ||
      last.runType !== this.runType ||
      last.testSpec !== this.testSpec ||
      last.refKitLot !== this.refKitLot ||
      last.columnSelectionKey !== nextColumnSelectionKey ||
      last.isPrintView !== this.isPrintView
    );

    if (shouldRebuild) {
      this.lastRebuildInputs = {
        reportContent: this.reportContent,
        sampleDataOriginal: this.sampleDataOriginal,
        sampleDataAdjusted: this.sampleDataAdjusted,
        sampleDataFailuresOriginal: this.sampleDataFailuresOriginal,
        sampleDataFailuresAdjusted: this.sampleDataFailuresAdjusted,
        calculationResponse: this.calculationResponse,
        calculationAdjustedResponse: this.calculationAdjustedResponse,
        panelSpecs: this.panelSpecs,
        calLots: this.calLots,
        qcLots: this.qcLots,
        runType: this.runType,
        testSpec: this.testSpec,
        refKitLot: this.refKitLot,
        columnSelectionKey: nextColumnSelectionKey,
        isPrintView: this.isPrintView
      };
    }
    return shouldRebuild;
  }

  private buildColumnSelectionKey(selections: string[] | null | undefined): string {
    if (!selections?.length) {
      return '';
    }
    return selections
      .slice()
      .sort()
      .join('|');
  }
  /**
   * Prepares assay details or failures data based on calculation result.
   * @param calculationResult Parsed calculation payload for either original or adjusted runs
   * @param samplePool Cached pull_sample_data stream that matches the calculation payload
   * @param label Indicates whether it's original or adjusted data
  */
  initialSetData(
    calculationResult: CalculationResult | null,
    samplePool: SampleSummaryResult[],
    label: string
  ): void {
    let result: AssayDetails[] = [];
    const runStatusMap = this.getRunStatusMap(calculationResult?.runStatus);
    const regressionStatsMap = this.getRegressionStatsMap(calculationResult?.regressionStats);
    const reportItemsMap = this.getReportItemsMap(calculationResult?.reportContentItems);
    const samplesByReportId = this.getSamplesByReportId(samplePool);

    this.filteredReportContent.forEach((sample) => {
      const reportItemId = sample.reportContentItemId ?? '';
      const calReportContentItems = reportItemsMap.get(reportItemId);
      const regressionStat = regressionStatsMap.get(reportItemId);
      const calRunStatus = runStatusMap.get(reportItemId);
      const calSamples = samplesByReportId.get(reportItemId) ?? [];

      if (calRunStatus && calReportContentItems && regressionStat) {
        result.push({
          reportContentItems: sample,
          reportItems: calReportContentItems,
          runStatus: calRunStatus,
          regressionStats: regressionStat,
          samples: calSamples,
          valueAssigned: label
        });

      } else {
        result.push({
          reportContentItems: sample,
          runStatus: calRunStatus,
          regressionStats: {
            ranges: [],
            reportContentItemId: '',
            numPassSamples: 0,
            numAlertSamples: 0,
            numFailSamples: 0,
            averageBeadCV: 0,
            averageBeadCount: 0,
            averageCountTime: 0,
            percentPositiveAgreement: 0,
            percentPositiveAgreementCI: '',
            percentNegativeAgreement: 0,
            percentNegativeAgreementCI: '',
            numConcordanceSamples: 0,
            numTruePositives: 0,
            numFalsePositives: 0,
            numEQRef: 0,
            numTrueNegatives: 0,
            numFalseNegatives: 0,
            numEQTest: 0,
            numEQ: 0,
            negativeSamplesPASS: 0,
            negativeSamplesFAILED: 0,
            negativeSamplesALERT: 0,
            positiveSamplesPASS: 0,
            positiveSamplesFAILED: 0,
            positiveSamplesAlert: 0,
            cutOffSamplesPASS: 0,
            cutOffSamplesFail: 0,
            CutOffSamplesAlert: 0
          },
          samples: [],
          valueAssigned: label
        });
      }
    });

    result = this.fillMissingSamplesFromCalculation(result, samplesByReportId);

    if (label === 'OriginalCalculate') {
      // A Final run has no unadjusted baseline, so no unadjusted columns are shown.
      this.calResponseFilteredData = this.isFinal ? [] : result;
    } else {
      this.calResponseFilteredAdjustedData = result;
    }
    if (this.calResponseFilteredAdjustedData.length > 0) {
      this.calResponseCombinedData = this.getCombinedData();
    } else {
      this.calResponseCombinedData = this.calResponseFilteredData;
    }
    this.calResponseCombinedBackupData = this.calResponseCombinedData;
    this.calResponseFailures = this.calResponseCombinedData;
  }

  getAllUniqueAccessions(results: AssayDetails[]): string[] {
    const accessions = results.flatMap(r =>
      r.samples.map(s => s.accessionNo).filter((acc): acc is string => acc !== undefined)
    );
    return Array.from(new Set(accessions));
  }
  /**
   * Adds any missing samples to each assay group to ensure all accessions are represented.
   * This ensures a complete matrix of samples across all groups for comparison.
   * @param results Current assay details per report item
   * @param samplesByReportId Cached lookup of samples keyed by reportContentItemId
   * @returns Updated array with missing samples filled in
   */
  fillMissingSamplesFromCalculation(
    results: AssayDetails[],
    samplesByReportId: Map<string, SampleSummaryResult[]>
  ): AssayDetails[] {
    const allAccessions = this.getAllUniqueAccessions(results);

    return results.map(group => {
      const existingAccessions = group.samples.map(s => s.accessionNo);
      const missingAccessions = allAccessions.filter(acc => !existingAccessions.includes(acc));
      const groupSamples = samplesByReportId.get(group.reportContentItems.reportContentItemId) ?? [];

      const missingSamples = missingAccessions.map(acc => {
        // Look up missing entries within the already-grouped samples so we stay O(1) per accession.
        return groupSamples.find(s => s.accessionNo === acc);
      }).filter((s): s is SampleSummaryResult => !!s); // Remove nulls

      const combinedSamples = [...group.samples, ...missingSamples];

      const sortedSamples = [...combinedSamples].sort((a, b) => {
        const accA = a.accessionNo ?? '';
        const accB = b.accessionNo ?? '';
        return accA.localeCompare(accB);
      });
      return {
        ...group,
        samples: sortedSamples
      };
    });
  }

  /**
   * Groups the cached pull_sample_data array by reportContentItemId once so we stop filtering
   * the entire dataset for every table cell.
   */
  private getSamplesByReportId(samples: SampleSummaryResult[] | undefined): Map<string, SampleSummaryResult[]> {
    if (!samples || samples.length === 0) {
      return new Map();
    }
    const cached = this.samplesByReportIdCache.get(samples);
    if (cached) {
      return cached;
    }
    const map = this.groupSamplesByReportId(samples);
    this.samplesByReportIdCache.set(samples, map);
    return map;
  }

  private groupSamplesByReportId(samples: SampleSummaryResult[]): Map<string, SampleSummaryResult[]> {
    const map = new Map<string, SampleSummaryResult[]>();
    samples.forEach(sample => {
      if (!sample.reportContentItemId) {
        return;
      }
      if (!map.has(sample.reportContentItemId)) {
        map.set(sample.reportContentItemId, []);
      }
      map.get(sample.reportContentItemId)?.push(sample);
    });
    return map;
  }

  /** Creates a dictionary for the runStatus array so lookups stay O(1) during table hydration. */
  private getRunStatusMap(runStatuses: RunStatus[] | undefined): Map<string, RunStatus> {
    if (!runStatuses || runStatuses.length === 0) {
      return new Map();
    }
    const cached = this.runStatusMapCache.get(runStatuses);
    if (cached) {
      return cached;
    }
    const map = this.buildRunStatusMap(runStatuses);
    this.runStatusMapCache.set(runStatuses, map);
    return map;
  }

  private buildRunStatusMap(runStatuses: RunStatus[] | undefined): Map<string, RunStatus> {
    const map = new Map<string, RunStatus>();
    runStatuses?.forEach(status => {
      if (status?.reportContentItemId) {
        map.set(status.reportContentItemId, status);
      }
    });
    return map;
  }

  /** Same as above but for regression stats, which power many of the failure summaries. */
  private getRegressionStatsMap(stats: RegressionStats[] | undefined): Map<string, RegressionStats> {
    if (!stats || stats.length === 0) {
      return new Map();
    }
    const cached = this.regressionStatsMapCache.get(stats);
    if (cached) {
      return cached;
    }
    const map = this.buildRegressionStatsMap(stats);
    this.regressionStatsMapCache.set(stats, map);
    return map;
  }

  private buildRegressionStatsMap(stats: RegressionStats[] | undefined): Map<string, RegressionStats> {
    const map = new Map<string, RegressionStats>();
    stats?.forEach(regression => {
      if (regression?.reportContentItemId) {
        map.set(regression.reportContentItemId, regression);
      }
    });
    return map;
  }

  /**
   * ReportItems do not carry the reportContentItemId, so we stringify their identifying fields
   * once (the same concatenation used throughout the legacy code) and store it in a lookup map.
   */
  private getReportItemsMap(reportItems: ReportItems[] | undefined): Map<string, ReportItems> {
    if (!reportItems || reportItems.length === 0) {
      return new Map();
    }
    const cached = this.reportItemsMapCache.get(reportItems);
    if (cached) {
      return cached;
    }
    const map = this.buildReportItemsMap(reportItems);
    this.reportItemsMapCache.set(reportItems, map);
    return map;
  }

  private buildReportItemsMap(reportItems: ReportItems[] | undefined): Map<string, ReportItems> {
    const map = new Map<string, ReportItems>();
    reportItems?.forEach(item => {
      map.set(this.buildReportItemKey(item), item);
    });
    return map;
  }

  /** Builds the string key calculatedReportContentItems used before (lot + bead region + instrument). */
  private buildReportItemKey(reportItem: ReportItems): string {
    return `${reportItem.kitLotNumber}${reportItem.BeadRegion}_${reportItem.instrumentID}`;
  }
  /**
   * Combines filtered original and adjusted data into a single dataset.
   * Used for display in the table.
   * @returns Merged array of AssayDetails
   */
  getCombinedData() {
    const combined = [
      ...(this.calResponseFilteredData ?? []),
      ...(this.calResponseFilteredAdjustedData ?? [])
    ];
    return combined.sort((left, right) => this.compareAssayDetails(left, right));
  }

  /**
   * Retrieves specific data for a sample by accession number and variable name.
   * @param materialItem Assay details containing samples
   * @param accessionNo Accession number of the sample
   * @param variableName Name of the variable to retrieve
   * @returns The value of the variable, or an empty string
   */
  getSampleData(materialItem: AssayDetails, accessionNo?: string, variableName?: string): string {
    if (!variableName) {
      return this.displayValue(null);
    }
    const sample = materialItem.samples.find(
      (cal: SampleSummaryResult) => cal.accessionNo === accessionNo
    );
    const value = this.getSampleFieldValue(sample, variableName);
    return this.displayValue(value, this.getColumnPrecision(materialItem));
  }

  /**
   * Reason a sample was auto-discarded, for the Discard Reason section. Falls back to the run status
   * error message for a sample the parser dropped entirely, which never reaches individualReps.
   */
  getSampleDiscardReason(materialItem: AssayDetails, accessionNo?: string): string {
    const sample = materialItem.samples.find(
      (cal: SampleSummaryResult) => cal.accessionNo === accessionNo
    );
    const reasons = new Set<string>();
    (sample?.individualReps ?? []).forEach(rep => {
      const formatted = formatDiscardReason(rep.isDiscarded, rep.discardReason);
      if (formatted.source === 'auto') {
        reasons.add(formatted.text);
      }
    });
    if (reasons.size) {
      return Array.from(reasons).join('; ');
    }
    const missingSample = materialItem.runStatus?.missingSamples?.find(
      missing => missing.accessionNo === accessionNo
    );
    if (missingSample?.discarded) {
      return this.displayValue(missingSample.errorMessage);
    }
    return this.displayValue(null);
  }

  /** True when there is a reason worth opening the dialog for. */
  hasDiscardReason(materialItem: AssayDetails, accessionNo?: string): boolean {
    return this.getSampleDiscardReason(materialItem, accessionNo) !== '-';
  }

  /** Splits a multi-reason cell onto separate lines for print, where nothing is clickable. */
  getDiscardReasonLines(materialItem: AssayDetails, accessionNo?: string): string[] {
    const reason = this.getSampleDiscardReason(materialItem, accessionNo);
    if (reason === '-') {
      return [reason];
    }
    return reason.split(';').map(segment => segment.trim()).filter(Boolean);
  }

  /**
   * Reasons can be long enough to overlap neighbouring columns, so the cell is truncated and the
   * full text is shown on click — the same treatment the Sample Errors screen gives error messages.
   */
  openDiscardReasonDialog(materialItem: AssayDetails, accessionNo?: string): void {
    const reason = this.getSampleDiscardReason(materialItem, accessionNo);
    if (reason === '-') {
      return;
    }
    const dialogData = new CommonDialogData();
    dialogData.title = `Discard Reason${accessionNo ? ` — ${accessionNo}` : ''}`;
    dialogData.message = reason;
    dialogData.okText = 'Close';
    dialogData.okAction = () => { };
    this.appUtilityService.displayAlertDialog(dialogData);
  }

  /** Sections in display order; each lists only the samples that fail its own check. */
  get sections(): FailureSection[] {
    if (this.sectionsSource !== this.calResponseCombinedData) {
      this.sectionsValue = this.buildSections(this.calResponseCombinedData);
      this.sectionsSource = this.calResponseCombinedData;
    }
    return this.sectionsValue;
  }

  trackBySection = (_index: number, section: FailureSection) => section.id;
  trackByRow = (_index: number, row: FailureSectionRow) => row.key;

  private sampleLookup(item: AssayDetails): Map<string, SampleSummaryResult> {
    const samples = item.samples ?? [];
    let lookup = this.sampleLookupCache.get(samples);
    if (!lookup) {
      const built = new Map<string, SampleSummaryResult>();
      samples.forEach(sample => {
        if (sample?.accessionNo && !built.has(sample.accessionNo)) {
          built.set(sample.accessionNo, sample);
        }
      });
      this.sampleLookupCache.set(samples, built);
      lookup = built;
    }
    return lookup;
  }

  private sampleFor(item: AssayDetails, accession: string): SampleSummaryResult | undefined {
    return this.sampleLookup(item).get(accession);
  }

  private buildSections(columns: AssayDetails[]): FailureSection[] {
    if (!columns.length) {
      return [];
    }
    const accessions = this.collectAccessions(columns);
    const anyColumn = (accession: string, test: (sample: SampleSummaryResult) => boolean) =>
      columns.some(item => {
        const sample = this.sampleFor(item, accession);
        return !!sample && test(sample);
      });
    const plainRows = (test: (sample: SampleSummaryResult) => boolean): FailureSectionRow[] =>
      accessions
        .filter(accession => anyColumn(accession, test))
        .map(accession => ({ key: accession, label: accession, title: '', accession }));

    const failsConcentration = (sample: SampleSummaryResult) => concentrationLevel(sample) !== null;
    const failsSdOrCv = (sample: SampleSummaryResult) => hasSdCvFailure(sample);

    const individualAccessions = accessions.filter(accession =>
      anyColumn(accession, sample => failsConcentration(sample) || failsSdOrCv(sample)));

    const sections: FailureSection[] = [
      {
        id: 'concentration',
        title: 'Sample Results - Concentration',
        status: 'concentration',
        rows: plainRows(failsConcentration).map(row => this.withSpecRange(row, columns))
      },
      { id: 'result', title: 'Result (Pass/Fail/Alert)', rows: plainRows(sample => resultLevel(sample) !== null) },
      { id: 'score', title: 'Sample Score', status: 'score', rows: plainRows(isFalseScore) },
      { id: 'sd', title: 'Sample SD', rows: plainRows(sample => sdCvLevels(sample).sd !== null) },
      { id: 'cv', title: 'Sample %CV', rows: plainRows(sample => sdCvLevels(sample).cv !== null) },
      { id: 'sdCv', title: 'Sample SD, %CV', status: 'sdCv', rows: plainRows(failsSdOrCv) },
      { id: 'individual', title: 'Individual Sample Results', rows: this.individualRows(individualAccessions, columns) },
      { id: 'replicateCount', title: 'Number of Replicates', rows: plainRows(isReplicateCountShort) },
      { id: 'replicatesOk', title: 'Number Replicates OK', status: 'replicates', rows: plainRows(isReplicateCountShort) },
      { id: 'assigned', title: 'Assigned Value', rows: plainRows(failsConcentration) },
      { id: 'discard', title: 'Discard Reason', rows: this.discardRows(columns) }
    ];
    // A row that is blank in every column would only render as an empty line under a header.
    return sections
      .map(section => ({ ...section, rows: section.rows.filter(row => this.rowHasValue(section, row, columns)) }))
      .filter(section => section.rows.length > 0);
  }

  private rowHasValue(section: FailureSection, row: FailureSectionRow, columns: AssayDetails[]): boolean {
    const blank = this.displayValue(null);
    return columns.some(item => this.getCellText(section, row, item) !== blank);
  }

  /** Samples in first-seen order across the displayed columns. */
  private collectAccessions(columns: AssayDetails[]): string[] {
    const accessions = new Set<string>();
    columns.forEach(item => (item.samples ?? []).forEach(sample => {
      if (sample?.accessionNo) {
        accessions.add(sample.accessionNo);
      }
    }));
    return Array.from(accessions);
  }

  /** Appends the breached spec range to the accession; the tooltip lists every range seen. */
  private withSpecRange(row: FailureSectionRow, columns: AssayDetails[]): FailureSectionRow {
    let worst: SampleSummaryResult | undefined;
    const details = new Set<string>();
    for (const item of columns) {
      const sample = this.sampleFor(item, row.accession);
      const level = concentrationLevel(sample);
      if (!sample || !level) {
        continue;
      }
      const described = describeSpecRanges(sample);
      if (described) {
        details.add(described);
      }
      if (!worst || (level === 'FAIL' && concentrationLevel(worst) !== 'FAIL')) {
        worst = sample;
      }
    }
    const range = breachedRange(worst);
    return { ...row, label: range ? `${row.accession} (${range})` : row.accession, title: Array.from(details).join('; ') };
  }

  private individualRows(accessions: string[], columns: AssayDetails[]): FailureSectionRow[] {
    const rows: FailureSectionRow[] = [];
    accessions.forEach(accession => {
      const repNums = new Set<number>();
      columns.forEach(item => {
        const sample = this.sampleFor(item, accession);
        if (this.qualifiesForIndividual(sample)) {
          keptReps(sample).forEach(rep => repNums.add(repNumber(rep)));
        }
      });
      Array.from(repNums).sort((a, b) => a - b).forEach(repNum => rows.push({
        key: `${accession}|${repNum}`,
        label: `${accession} - Rep ${repNum}`,
        title: '',
        accession,
        repNum
      }));
    });
    return rows;
  }

  private qualifiesForIndividual(sample: SampleSummaryResult | undefined): boolean {
    return !!sample && (concentrationLevel(sample) !== null || hasSdCvFailure(sample));
  }

  private discardRows(columns: AssayDetails[]): FailureSectionRow[] {
    const accessions = new Set(this.collectAccessions(columns));
    columns.forEach(item => (item.runStatus?.missingSamples ?? []).forEach(missing => {
      if (missing.discarded && missing.accessionNo) {
        accessions.add(missing.accessionNo);
      }
    }));
    return Array.from(accessions)
      .filter(accession => columns.some(item => this.hasDiscardReason(item, accession)))
      .map(accession => ({ key: accession, label: accession, title: '', accession }));
  }

  /** Text for one cell; '-' where this column's sample does not fail the section's check. */
  getCellText(section: FailureSection, row: FailureSectionRow, item: AssayDetails): string {
    const blank = this.displayValue(null);
    if (section.id === 'discard') {
      return this.getSampleDiscardReason(item, row.accession);
    }
    const sample = this.sampleFor(item, row.accession);
    if (!sample) {
      return blank;
    }
    switch (section.id) {
      case 'concentration':
        return concentrationLevel(sample) ? this.getSampleData(item, row.accession, 'meanFinalConc') : blank;
      case 'result':
        return resultLevel(sample) ? this.getSampleData(item, row.accession, 'resultStatus') : blank;
      case 'score':
        return isFalseScore(sample) ? this.getSampleData(item, row.accession, 'testScore') : blank;
      case 'sd':
        return sdCvLevels(sample).sd ? this.getSampleData(item, row.accession, 'repSD') : blank;
      case 'cv':
        return sdCvLevels(sample).cv ? this.getSampleData(item, row.accession, 'repCV') : blank;
      case 'sdCv':
        return hasSdCvFailure(sample) ? this.getSampleData(item, row.accession, 'resultRepOk') : blank;
      case 'individual': {
        if (!this.qualifiesForIndividual(sample)) {
          return blank;
        }
        const rep = keptReps(sample).find(candidate => repNumber(candidate) === row.repNum);
        return rep ? this.displayValue(repConcentration(rep), this.getColumnPrecision(item)) : blank;
      }
      case 'replicateCount':
        return isReplicateCountShort(sample) ? this.getSampleData(item, row.accession, 'finalRepCount') : blank;
      case 'replicatesOk':
        return isReplicateCountShort(sample) ? this.getSampleData(item, row.accession, 'repCountOk') : blank;
      case 'assigned':
        return concentrationLevel(sample) ? this.getSampleData(item, row.accession, 'assignedValue') : blank;
    }
  }

  /** Alert and fail take the kit's yellow and red; sections with no highlight return ''. */
  getCellClass(section: FailureSection, row: FailureSectionRow, item: AssayDetails): string {
    if (section.id === 'discard') {
      return this.hasDiscardReason(item, row.accession) ? DISCARD_TEXT_CLASS : '';
    }
    const sample = this.sampleFor(item, row.accession);
    if (!sample) {
      return '';
    }
    switch (section.id) {
      case 'concentration':
        return this.statusClass(concentrationLevel(sample));
      case 'result':
        return this.statusClass(resultLevel(sample));
      case 'score':
        return isFalseScore(sample) ? this.statusClass('FAIL') : '';
      case 'sd':
        return this.statusClass(sdCvLevels(sample).sd);
      case 'cv':
        return this.statusClass(sdCvLevels(sample).cv);
      case 'sdCv': {
        const { sd, cv } = sdCvLevels(sample);
        return this.statusClass(sd === 'FAIL' || cv === 'FAIL' ? 'FAIL' : sd ?? cv);
      }
      case 'replicateCount':
      case 'replicatesOk':
        return isReplicateCountShort(sample) ? this.statusClass('FAIL') : '';
      default:
        return '';
    }
  }

  private statusClass(level: string | null): string {
    return level ? getStatusClassFromResult(level) : '';
  }

  /**
   * Derives the overall kit status based on various run validity flags.
   * @param materialItem AssayDetails containing run status information
   * @param name Name of the validation section ("RunValidity")
   * @returns The overall result status ("PASS", "FAIL", "ALERT")
   */
  getKitStatusRunValidity(materialItem: AssayDetails, name: string) {
    let overallStatus = "PASS";
    let results: (string | undefined)[] = [];
    if (name === "RunValidity") {
      results = [
        materialItem?.runStatus?.calibratorSpecStatus,
        materialItem?.runStatus?.patientSampleSpecStatus,
        materialItem?.runStatus?.calCurveInformation?.calCurveValidity,
        materialItem?.runStatus?.calCurveInformation?.calReplicateValidity,
        materialItem?.runStatus?.sampleReplicateCountValidity
      ];
    }
    const filteredResults = results.filter((result): result is string => result !== undefined);
    overallStatus = this.resultPassFailAlert(filteredResults);
    return overallStatus;
  }

  /**
   * Toggles visibility for a specific section in the UI.
   * @param sectionId Id of the section to toggle
   */
  toggleSectionDisplay(sectionId: string) {
    if (!this.collapsedSections.delete(sectionId)) {
      this.collapsedSections.add(sectionId);
    }
    this.scheduleEmptyRowPrune();
  }

  isSectionExpanded(sectionId: string): boolean {
    return !this.collapsedSections.has(sectionId);
  }

  /**
   * Expands or collapses all sections in the UI.
   * @param isExpand True to expand all, false to collapse all
   */
  toggleAllSections(isExpand: boolean) {
    this.collapsedSections = isExpand ? new Set<string>() : new Set(this.sections.map(section => section.id));
    if (isExpand) {
      this.visibleSectionCount = Number.MAX_SAFE_INTEGER;
    }
    this.scheduleEmptyRowPrune();
  }

  private scheduleEmptyRowPrune(): void {
    if (!this.assayDetailsTableRef?.nativeElement) {
      return;
    }
    window.setTimeout(() => this.pruneEmptyRows(), 0);
  }

  private pruneEmptyRows(): void {
    const tables = this.assayDetailsTableRefs?.toArray().map(ref => ref.nativeElement)
      ?? (this.assayDetailsTableRef ? [this.assayDetailsTableRef.nativeElement] : []);
    tables.forEach(tableEl => this.pruneEmptyRowsIn(tableEl));
  }

  private pruneEmptyRowsIn(tableEl: HTMLTableElement): void {
    const rows = Array.from(tableEl.querySelectorAll('tr')) as HTMLTableRowElement[];
    rows.forEach(row => {
      if (
        row.classList.contains('subsection-header') ||
        row.classList.contains('subsection-header-print2') ||
        row.classList.contains('subsection-header-print') ||
        row.classList.contains('subsection-header-print-firstrow')
      ) {
        row.style.removeProperty('display');
        return;
      }
      const cells = Array.from(row.querySelectorAll('td'));
      if (cells.length <= 1) {
        row.style.removeProperty('display');
        return;
      }
      const dataCells = cells.slice(1);
      const hasValue = dataCells.some(cell => {
        const text = (cell.textContent ?? '').trim();
        return text !== '' && text !== '-';
      });
      row.style.display = hasValue ? '' : 'none';
    });
  }

  shouldRenderSection(index: number): boolean {
    return index < this.visibleSectionCount;
  }
  /**
   * Filters assay details for the "Assay Failures" tab based on selected columns.
   * @param selectedColumns Columns that are currently selected in the UI
   */
  handleSelectColumnsFailures(selectedColumns: ColumnSelection[]) {
    this.selectedColumns = selectedColumns;
    this.calResponseCombinedData = this.calResponseFailures.filter((item) =>
      this.selectedColumns.some((column) => this.matchesSelection(column, item.reportContentItems))
    );
  }

  /** Columns chosen in the print preview bar; held so a rebuild cannot silently widen the print. */
  handlePrintColumnSelection(selectedColumns: ColumnSelection[]): void {
    this.printColumnSelection = selectedColumns;
    this.handleSelectColumnsFailures(selectedColumns);
  }

  /** Identifies the document in the print preview bar, beside the study name. */
  get printContextLine(): string {
    return [
      this.runType,
      this.testSpec ? `Test Spec ${this.testSpec}` : '',
      this.specVersion ? `Spec Rev ${this.specVersion}` : '',
      this.operatorName
    ].filter(Boolean).join(' · ');
  }

  private applyStoredColumnSelection(columnKeys: string[] | null | undefined): void {
    if (!this.tableColumns?.length) {
      this.selectedColumns = [];
      return;
    }
    const selectionSet = new Set((columnKeys ?? []).filter(Boolean));
    const hasMatches = this.tableColumns.some(column =>
      selectionSet.has(column.columnKey ?? column.columnName ?? '')
    );
    const useDefault = selectionSet.size === 0 || !hasMatches;
    this.tableColumns = this.tableColumns.map(column => ({
      ...column,
      isSelected: useDefault ? true : selectionSet.has(column.columnKey ?? column.columnName ?? '')
    }));
    this.selectedColumns = this.tableColumns;
    this.calResponseCombinedData = this.calResponseFailures.filter((item) =>
      this.selectedColumns.some((column) => this.matchesSelection(column, item.reportContentItems))
    );
  }

  /**
   * Handles the open/close status of the print menu.
   * If opened, expands all sections.
   * @param isMenuOpen Boolean indicating whether the print menu is open
   */
  handleMenuOpenStatus(isMenuOpen: boolean) {
    this.isPrintMode = isMenuOpen;

    if (isMenuOpen) {
      this.toggleAllSections(isMenuOpen);
    }
  }
  /**
   * Returns the current date formatted for display.
   * @returns A formatted date string
   */
  getDate(): string {
    return this.appUtilityService.formatDate(new Date());
  }

  private matchesSelection(column: ColumnSelection, reportContentItem: ReportContentItem): boolean {
    if (!column.isSelected) {
      return false;
    }

    if (!column.columnKey && !column.instrumentId && column.beadRegion === undefined) {
      return column.columnName === reportContentItem.lot;
    }

    const key = this.buildColumnKey(reportContentItem.lot, reportContentItem.runId, reportContentItem.region);
    return (column.columnKey ?? column.columnName) === key;
  }

  private buildColumnKey(kitLot: string | undefined, instrumentId: string | undefined, beadRegion: number | undefined): string {
    return `${kitLot ?? ''}||${instrumentId ?? ''}||${beadRegion ?? ''}`;
  }

  private sortReportContent(reportContent: ReportContentItem[]): ReportContentItem[] {
    return [...(reportContent ?? [])].sort((a, b) => this.compareReportContentItems(a, b));
  }

  private compareReportContentItems(a: ReportContentItem, b: ReportContentItem): number {
    const baseCompare = this.compareReportContentBase(a, b);
    if (baseCompare !== 0) {
      return baseCompare;
    }

    const adjustmentCompare =
      this.getReportContentAdjustmentRank(a) - this.getReportContentAdjustmentRank(b);
    if (adjustmentCompare !== 0) {
      return adjustmentCompare;
    }

    const runCompare = (a.runId ?? '').localeCompare(b.runId ?? '');
    if (runCompare !== 0) {
      return runCompare;
    }

    return (a.assay ?? '').localeCompare(b.assay ?? '');
  }

  private compareReportContentBase(a: ReportContentItem, b: ReportContentItem): number {
    const regionCompare = this.compareBeadRegions(a.region, b.region);
    if (regionCompare !== 0) {
      return regionCompare;
    }

    const refLot = (this.refKitLot ?? '').trim();
    const aLot = (a.lot ?? '');
    const bLot = (b.lot ?? '');
    if (refLot) {
      const aIsRef = aLot === refLot;
      const bIsRef = bLot === refLot;
      if (aIsRef !== bIsRef) {
        return aIsRef ? -1 : 1;
      }
    }
    const kitCompare = aLot.localeCompare(bLot);
    if (kitCompare !== 0) {
      return kitCompare;
    }

    return (a.assay ?? '').localeCompare(b.assay ?? '');
  }

  private compareAssayDetails(left: AssayDetails, right: AssayDetails): number {
    const baseCompare = this.compareReportContentBase(left.reportContentItems, right.reportContentItems);
    if (baseCompare !== 0) {
      return baseCompare;
    }

    const adjustCompare = this.getAssayAdjustmentRank(left) - this.getAssayAdjustmentRank(right);
    if (adjustCompare !== 0) {
      return adjustCompare;
    }

    const runCompareWithMean = this.compareRunIdWithMeanLast(left, right);
    if (runCompareWithMean !== 0) {
      return runCompareWithMean;
    }

    return 0;
  }

  private compareRunIdWithMeanLast(left: AssayDetails, right: AssayDetails): number {
    const leftIsMean = this.isMeanReportItem(left.reportContentItems.reportContentItemId);
    const rightIsMean = this.isMeanReportItem(right.reportContentItems.reportContentItemId);
    if (leftIsMean !== rightIsMean) {
      return leftIsMean ? 1 : -1;
    }
    return (left.reportContentItems.runId ?? '').localeCompare(right.reportContentItems.runId ?? '');
  }

  private getAssayAdjustmentRank(item: AssayDetails): number {
    return item.valueAssigned === 'AdjustedCalculate' ? 1 : 0;
  }

  private getReportContentAdjustmentRank(item: ReportContentItem): number {
    return item?.['valueAssigned'] === 'AdjustedCalculate' ? 1 : 0;
  }

  private isMeanReportItem(reportContentItemId: string | undefined): boolean {
    return !!reportContentItemId && reportContentItemId.startsWith('Mean_');
  }

  private compareBeadRegions(left: string | number | undefined, right: string | number | undefined): number {
    const leftRegion = this.parseRegionValue(left);
    const rightRegion = this.parseRegionValue(right);
    const leftInternal = this.internalQcBeadRegions.has(leftRegion);
    const rightInternal = this.internalQcBeadRegions.has(rightRegion);
    if (leftInternal !== rightInternal) {
      return leftInternal ? 1 : -1;
    }
    return leftRegion - rightRegion;
  }

  private parseRegionValue(region: string | number | undefined): number {
    if (typeof region === 'number') {
      return Number.isNaN(region) ? 0 : region;
    }
    const parsed = Number(region);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Retrieves metadata value for a given key from the first patient file.
   * @param key Key of the metadata field
   * @returns Value of the specified metadata field or an empty string
   */
  getPatientFileData<K extends keyof FileMetaData>(key: K): string {
    if (this.patientFileMetadata.length) {
      return this.displayValue(this.patientFileMetadata[0][key]);
    }
    return this.displayValue(null);
  }
  /**
   * Gets the unit string associated with a given bead region number.
   * @param region The bead region number
   * @returns The corresponding unit string or an empty string
   */
  getUnitsByRegion(region: number): string {
    if (!this.panelSpecs || !Array.isArray(this.panelSpecs?.Assay_Specs)) {
      return '';
    }

    const match = this.panelSpecs?.Assay_Specs.find(spec => spec.bead_region === region);
    return match?.units ?? '';
  }
  /**
   * Formats values for display in the template, substituting '-' for blanks.
   * @param value Raw value from the dataset
   * @returns Formatted string suitable for rendering
   */
  displayValue(value: unknown, precision?: number): string {
    if (value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed || trimmed.toLowerCase() === 'none') {
        return '-';
      }
      return trimmed;
    }
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) {
        return '-';
      }
      if (typeof precision === 'number' && precision >= 0) {
        return this.formatNumberWithPrecision(value, precision);
      }
      return value.toString();
    }
    return String(value);
  }

  private formatNumberWithPrecision(value: number, precision: number): string {
    if (!Number.isFinite(value)) {
      return '-';
    }
    const maxDecimals = Math.min(Math.max(precision, 0), 6);
    const factor = 10 ** maxDecimals;
    const truncated = Math.round(value * factor) / factor;
    const fixed = truncated.toFixed(maxDecimals);
    // Only trim a fractional tail — at precision 0 there is no decimal point, and stripping
    // trailing zeros from a whole number would turn 100 into 1.
    return fixed.includes('.') ? fixed.replace(/\.?0+$/, '') : fixed;
  }

  getSectionStatusLabel(section: 'concentration' | 'score' | 'sdCv' | 'replicates'): string {
    const source = this.calResponseFailuresBaseline;
    if (section === 'score') {
      return source.some(item => (item.samples ?? []).some(isFalseScore)) ? 'FAIL' : 'PASS';
    }
    const statuses = this.collectSectionStatuses(section, source);
    let hasFail = false;
    let hasAlert = false;
    let hasPass = false;
    statuses.forEach(status => {
      const normalized = normalizeStatusValue(status);
      if (normalized === 'FAIL') {
        hasFail = true;
      } else if (normalized === 'ALERT') {
        hasAlert = true;
      } else if (normalized === 'PASS') {
        hasPass = true;
      }
    });
    if (hasFail) {
      return 'FAIL';
    }
    if (hasAlert) {
      return 'ALERT';
    }
    if (hasPass) {
      return 'PASS';
    }
    return 'N/A';
  }

  getSectionStatusClass(section: 'concentration' | 'score' | 'sdCv' | 'replicates'): string {
    const status = this.getSectionStatusLabel(section);
    if (status === 'FAIL') {
      return 'status-label status-label--fail';
    }
    if (status === 'ALERT') {
      return 'status-label status-label--alert';
    }
    if (status === 'PASS') {
      return 'status-label status-label--pass';
    }
    return 'status-label status-label--na';
  }

  private collectSectionStatuses(
    section: 'concentration' | 'score' | 'sdCv' | 'replicates',
    source: AssayDetails[]
  ): string[] {
    const statuses: string[] = [];
    const fieldName = this.getSectionStatusField(section);
    source.forEach(item => {
      (item.samples ?? []).forEach(sample => {
        if (!fieldName) {
          return;
        }
        const value = this.getSampleFieldValue(sample, fieldName);
        if (typeof value === 'string') {
          statuses.push(value);
        } else if (typeof value === 'number') {
          statuses.push(String(value));
        }
      });
    });
    return statuses;
  }

  private getSectionStatusField(section: 'concentration' | 'score' | 'sdCv' | 'replicates'): string {
    switch (section) {
      case 'concentration':
        return 'acceptanceRangeStatus';
      case 'score':
        return 'testScore';
      case 'sdCv':
        return 'resultRepOk';
      case 'replicates':
        return 'repCountOk';
    }
  }

  private getSampleFieldValue(sample: SampleSummaryResult | undefined, variableName: string): unknown {
    if (!sample) {
      return undefined;
    }
    const record = sample as Record<string, unknown>;
    if (variableName in record) {
      return record[variableName];
    }
    const alias = this.sampleFieldAliases[variableName];
    if (alias && alias in record) {
      return record[alias];
    }
    return undefined;
  }

  private getColumnPrecision(materialItem: AssayDetails): number | undefined {
    const precision = materialItem?.reportContentItems?.userDefinedDecimalPrecision
      ?? materialItem?.reportContentItems?.resultDecimalPrecision;
    return typeof precision === 'number' ? precision : undefined;
  }
  /**
   * Exports the assay table currently rendered in the DOM to a CSV file.
   */
  exportTableToCsv(): void {
    const table = document.getElementById(this.tableId) as HTMLTableElement;
    if (!table) {
      console.error('Table not found!');
      return;
    }

    let csv = '';
    for (const row of Array.from(table.rows)) {
      const cols = Array.from(row.cells).map(cell => {
        let text = cell.innerText.trim().replace(/"/g, '""');
        if (text.includes(',') || text.includes('"') || text.includes('\n')) {
          text = `"${text}"`;
        }
        return text;
      });
      csv += cols.join(',') + '\n';
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.setAttribute('href', url);
    link.setAttribute('download', 'assays-table-export.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  /** Opens the print preview for this view. */
  navigateToPrintView() {
    this.router.navigateByUrl('/(print:print/preview-assay-failures)');
  }

  private setupHeaderHeightObserver(): void {
    const scrollEl = this.assayDetailsScrollRef?.nativeElement;
    const tableEl = this.assayDetailsTableRef?.nativeElement;
    if (!scrollEl || !tableEl) {
      return;
    }

    const headerEl = tableEl.querySelector('thead');
    if (!headerEl) {
      return;
    }

    const updateHeaderHeight = () => {
      const height = headerEl.getBoundingClientRect().height;
      if (height > 0) {
        scrollEl.style.setProperty('--assay-details-header-height', `${Math.ceil(height)}px`);
      }
    };

    updateHeaderHeight();

    if (typeof ResizeObserver !== 'undefined') {
      this.headerResizeObserver = new ResizeObserver(() => updateHeaderHeight());
      this.headerResizeObserver.observe(headerEl);
    } else {
      const onResize = () => updateHeaderHeight();
      window.addEventListener('resize', onResize);
      this.destroy$.subscribe(() => window.removeEventListener('resize', onResize));
    }
  }
  /**
    * Lifecycle hook: Component cleanup.
    * Ensures subscriptions are properly unsubscribed.
    */
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.headerResizeObserver?.disconnect();
    this.cancelDeferredSections();
  }
}
